import { useState, useCallback } from 'react';
import api from '../../services/api';

// Chave para localStorage
const PREFERENCE_KEY = 'sentimentPropagation_preference';

// Valores de preferência
export const PROPAGATION_PREFERENCES = {
  ASK: 'ask',           // Sempre perguntar
  ALWAYS: 'always',     // Sempre propagar automaticamente
  NEVER: 'never'        // Nunca propagar
};

/**
 * Hook para gerenciar propagação de sentimento para artigos similares
 *
 * Fluxo:
 * 1. Usuário muda sentimento
 * 2. Se preferência = 'ask', busca similares (preview) e abre dialog
 * 3. Se preferência = 'always', atualiza artigo + similares automaticamente
 * 4. Se preferência = 'never', atualiza apenas o artigo
 *
 * @param {Function} setArticles - Função para atualizar estado de artigos
 * @param {Function} showSnackbar - Função para mostrar notificações
 */
const useSentimentPropagation = (setArticles, showSnackbar) => {
  // Estado do dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [pendingSentiment, setPendingSentiment] = useState(null);
  const [pendingArticleId, setPendingArticleId] = useState(null);
  const [similarArticles, setSimilarArticles] = useState([]);

  // Notificação helper
  const notify = useCallback((message, severity = 'success') => {
    if (typeof showSnackbar === 'function') {
      showSnackbar(message, severity);
    }
  }, [showSnackbar]);

  // Ler preferência do localStorage
  const getPreference = useCallback(() => {
    try {
      return localStorage.getItem(PREFERENCE_KEY) || PROPAGATION_PREFERENCES.ASK;
    } catch {
      return PROPAGATION_PREFERENCES.ASK;
    }
  }, []);

  // Salvar preferência no localStorage
  const setPreference = useCallback((pref) => {
    try {
      localStorage.setItem(PREFERENCE_KEY, pref);
    } catch (e) {
      console.error('Erro ao salvar preferência:', e);
    }
  }, []);

  // Atualizar artigo no estado local (optimistic update)
  const updateArticleInState = useCallback((articleId, sentiment) => {
    setArticles(prevArticles =>
      prevArticles.map(article =>
        article._id === articleId
          ? { ...article, sentiment, sentimentManual: true }
          : article
      )
    );
  }, [setArticles]);

  const mergeArticleInState = useCallback((updatedArticle) => {
    if (!updatedArticle?._id) return;
    setArticles(prevArticles =>
      prevArticles.map(article =>
        article._id === updatedArticle._id
          ? { ...article, ...updatedArticle }
          : article
      )
    );
  }, [setArticles]);

  // Atualizar múltiplos artigos no estado local
  const updateMultipleInState = useCallback((articleIds, sentiment) => {
    setArticles(prevArticles =>
      prevArticles.map(article =>
        articleIds.includes(article._id)
          ? { ...article, sentiment, sentimentManual: true }
          : article
      )
    );
  }, [setArticles]);

  // Aplicar sentimento apenas ao artigo principal
  const applySentimentSingle = useCallback(async (articleId, sentiment) => {
    try {
      const response = await api.put(`/articles/${articleId}/sentiment`, {
        sentiment,
        propagate: false
      });
      mergeArticleInState(response.data?.data);
      return true;
    } catch (error) {
      console.error('Erro ao atualizar sentimento:', error);
      notify('Erro ao atualizar sentimento', 'error');
      return false;
    }
  }, [mergeArticleInState, notify]);

  // Aplicar sentimento ao artigo + similares
  const applySentimentWithPropagation = useCallback(async (articleId, sentiment) => {
    try {
      const response = await api.put(`/articles/${articleId}/sentiment`, {
        sentiment,
        propagate: true,
        minScore: 90
      });

      mergeArticleInState(response.data?.data);

      // Atualizar artigos similares no estado local
      if (response.data?.propagation?.articles?.length > 0) {
        const similarIds = response.data.propagation.articles.map(a => a._id);
        updateMultipleInState(similarIds, sentiment);

        const count = response.data.propagation.updated;
        if (count > 0) {
          notify(`Sentimento aplicado a ${count + 1} artigo${count > 0 ? 's' : ''}`, 'success');
        }
      }
      return true;
    } catch (error) {
      console.error('Erro ao propagar sentimento:', error);
      notify('Erro ao propagar sentimento', 'error');
      return false;
    }
  }, [mergeArticleInState, updateMultipleInState, notify]);

  // Buscar artigos similares (preview mode)
  const fetchSimilarArticles = useCallback(async (articleId, sentiment) => {
    try {
      const response = await api.put(`/articles/${articleId}/sentiment`, {
        sentiment,
        preview: true,
        minScore: 90
      });

      return response.data?.similarArticles || [];
    } catch (error) {
      console.error('Erro ao buscar similares:', error);
      return [];
    }
  }, []);

  /**
   * Função principal para atualizar sentimento com fluxo de confirmação
   *
   * @param {string} articleId - ID do artigo
   * @param {string} sentiment - Sentimento (positive, neutral, negative)
   */
  const updateSentiment = useCallback(async (articleId, sentiment) => {
    // 1. Optimistic update do artigo principal
    let previousSentiment = null;
    setArticles(prevArticles => {
      const article = prevArticles.find(a => a._id === articleId);
      previousSentiment = article?.sentiment;
      return prevArticles.map(a =>
        a._id === articleId ? { ...a, sentiment, sentimentManual: true } : a
      );
    });

    // 2. Verificar preferência do usuário
    const preference = getPreference();

    // 3. NUNCA propagar - apenas atualiza o artigo
    if (preference === PROPAGATION_PREFERENCES.NEVER) {
      const success = await applySentimentSingle(articleId, sentiment);
      if (!success) {
        // Rollback
        updateArticleInState(articleId, previousSentiment);
      }
      return;
    }

    // 4. SEMPRE propagar - atualiza artigo + similares automaticamente
    if (preference === PROPAGATION_PREFERENCES.ALWAYS) {
      const success = await applySentimentWithPropagation(articleId, sentiment);
      if (!success) {
        // Rollback
        updateArticleInState(articleId, previousSentiment);
      }
      return;
    }

    // 5. PERGUNTAR - buscar similares e abrir dialog
    setDialogLoading(true);
    const similares = await fetchSimilarArticles(articleId, sentiment);

    // Se não encontrou similares, apenas atualiza o artigo
    if (similares.length === 0) {
      const success = await applySentimentSingle(articleId, sentiment);
      if (!success) {
        updateArticleInState(articleId, previousSentiment);
      }
      setDialogLoading(false);
      return;
    }

    // Abrir dialog para confirmação
    setPendingArticleId(articleId);
    setPendingSentiment(sentiment);
    setSimilarArticles(similares);
    setDialogOpen(true);
    setDialogLoading(false);
  }, [
    setArticles,
    getPreference,
    applySentimentSingle,
    applySentimentWithPropagation,
    fetchSimilarArticles,
    updateArticleInState
  ]);

  // Handler quando usuário confirma propagação
  const handleDialogConfirm = useCallback(async (savePreference) => {
    setDialogLoading(true);

    // Salvar preferência se solicitado
    if (savePreference === 'always') {
      setPreference(PROPAGATION_PREFERENCES.ALWAYS);
    }

    // Aplicar sentimento com propagação
    await applySentimentWithPropagation(pendingArticleId, pendingSentiment);

    // Fechar dialog
    setDialogOpen(false);
    setDialogLoading(false);
    setPendingArticleId(null);
    setPendingSentiment(null);
    setSimilarArticles([]);
  }, [pendingArticleId, pendingSentiment, applySentimentWithPropagation, setPreference]);

  // Handler quando usuário cancela (apenas este artigo)
  const handleDialogCancel = useCallback(async (savePreference) => {
    setDialogLoading(true);

    // Salvar preferência se solicitado
    if (savePreference === 'never') {
      setPreference(PROPAGATION_PREFERENCES.NEVER);
    }

    // Aplicar sentimento apenas ao artigo principal
    await applySentimentSingle(pendingArticleId, pendingSentiment);

    // Fechar dialog
    setDialogOpen(false);
    setDialogLoading(false);
    setPendingArticleId(null);
    setPendingSentiment(null);
    setSimilarArticles([]);
  }, [pendingArticleId, pendingSentiment, applySentimentSingle, setPreference]);

  // Handler para fechar dialog (sem ação)
  const handleDialogClose = useCallback(() => {
    // Apenas fecha sem fazer nada (artigo já foi atualizado otimisticamente)
    setDialogOpen(false);
    setPendingArticleId(null);
    setPendingSentiment(null);
    setSimilarArticles([]);
  }, []);

  return {
    // Estado do dialog
    dialogOpen,
    dialogLoading,
    pendingSentiment,
    similarArticles,

    // Handlers do dialog
    handleDialogConfirm,
    handleDialogCancel,
    handleDialogClose,

    // Função principal
    updateSentiment,

    // Utilitários de preferência
    getPreference,
    setPreference,
    PROPAGATION_PREFERENCES
  };
};

export default useSentimentPropagation;
